import { max } from 'lodash-es';
import moment from 'moment';
import { type JSX, useEffect, useState } from 'react';

import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_SUBMIT } from 'Config/Icons';
import { DUE_DILIGENCE_FINAL_REVIEW_RECOMMENDED_IRQ_DUE_DATE } from 'Config/Tooltips';
import { getRecommendedReassessmentIntervalInMonths } from 'Helpers/UcrmTprm';
import { AssessmentSubjectArchiveBase, AssessmentSubjectBase } from 'Models/AssessmentSubject';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceTimeframes, ReassessmentIntervals } from 'Models/DueDiligenceWorkflow';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating, riskRatingAsString } from 'Models/RiskRating';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';

/** @param assessmentSubject Never actually an archived version here (the user can't click the Submit button when viewing a historical Final Review). */
export interface DueDiligenceFinalReviewSubmitConfirmationModalProps {
    assessments: TprmOrUcrmDueDiligenceAssessment[];
    assessmentSubject: AssessmentSubjectBase | AssessmentSubjectArchiveBase;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    hideModal: () => void;
    inherentRiskScore: RiskRating;
    module: DueDiligenceModule;
    onSubmit: (newAssessmentDueDate: Date | undefined, newIrqTargetCompletionDate: Date | undefined) => Promise<void>;
    timeframes: DueDiligenceTimeframes;
}

/**
 * A modal that asks the user to confirm the submission of the Final Review of an assessment subject.
 * Also allows the user to select due dates for the next due diligence workflow, as well as the IRQ (since completing it is the first step of a workflow). Recommended dates are calculated and displayed.
 */
export const DueDiligenceFinalReviewSubmitConfirmationModal = (props: DueDiligenceFinalReviewSubmitConfirmationModalProps): JSX.Element => {
    const assessmentSubjectTerm = props.module === ProductModule.UCRM ? 'use case' : 'third-party service';
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    const [recommendedIrqTargetCompletionDate, setRecommendedIrqTargetCompletionDate] = useState<Date | undefined | null>();
    const [recommendedAssessmentDueDate, setRecommendedAssessmentDueDate] = useState<Date | undefined | null>();
    const [newIrqTargetCompletionDate, setNewIrqTargetCompletionDate] = useState<Date>();
    const [newAssessmentDueDate, setNewAssessmentDueDate] = useState<Date>();

    useEffect(() => {
        const loadConfigurations = async () => {
            try {
                const configurations = (await props.dueDiligenceAssessmentConfigurationApi.getAllAssessmentConfigurations()).data;

                const _recommendedAssessmentDueDate = getRecommendedAssessmentDueDate(props.inherentRiskScore, props.timeframes.service_reassessment_intervals);
                const _recommendedIrqTargetCompletionDate = getRecommendedIrqTargetDate(_recommendedAssessmentDueDate ?? undefined, configurations, props.timeframes);

                setRecommendedIrqTargetCompletionDate(_recommendedIrqTargetCompletionDate);
                setNewIrqTargetCompletionDate(_recommendedIrqTargetCompletionDate ?? undefined);

                setRecommendedAssessmentDueDate(_recommendedAssessmentDueDate);
                setNewAssessmentDueDate(_recommendedAssessmentDueDate ?? undefined);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        loadConfigurations();
    }, [props.dueDiligenceAssessmentConfigurationApi, props.inherentRiskScore, props.timeframes]);

    if (recommendedIrqTargetCompletionDate === undefined || recommendedAssessmentDueDate === undefined) {
        return <ConfirmationModal isLoading headerText="Submit Final Review" hideModal={props.hideModal} failureMessage={loadingErrorOccurred ? GENERIC_ERROR_MESSAGE : undefined} />;
    }

    return (
        <ConfirmationModal
            operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE}
            headerText="Submit Final Review"
            areYouSureText="Are you sure you want to submit the final review?"
            buttonText="Submit"
            buttonLoadingText="Submitting..."
            buttonIcon={ICON_SUBMIT}
            performOperation={async () => {
                await props.onSubmit(newAssessmentDueDate, newIrqTargetCompletionDate);
                return 'Final review submitted.';
            }}
            hideModal={props.hideModal}
        >
            <Text>{`Submitting the final review will complete the due diligence workflow and schedule the next workflow for this ${assessmentSubjectTerm}.`}</Text>
            {props.assessmentSubject.assessment_workflow_setup.common_assessment_parent !== undefined && (
                <Text>
                    This {assessmentSubjectTerm} is using shared due diligence assessments provided by {props.assessmentSubject.assessment_workflow_setup.common_assessment_parent.name}. Because a snapshot of the shared due diligence assessments will be recorded when this final review is submitted, it is recommended that you ensure the assessments provided by {props.assessmentSubject.assessment_workflow_setup.common_assessment_parent.name} have been finalized before this final review is submitted.
                </Text>
            )}
            {props.assessments.some((assessment) => assessment.assessment_submitted_timestamp === undefined) && <Text color="red">Some assessments have not yet been submitted. It is recommended that all assessments be reviewed and submitted before submitting the final review; once the final review is submitted, assessments cannot be edited.</Text>}
            <div>
                <FormFieldDatePicker formFieldId="new-irq-due-date" formFieldLabel="Inherent Risk Questionnaire Due Date" selected={newIrqTargetCompletionDate} handleChange={(value: Date | undefined) => setNewIrqTargetCompletionDate(value)} dateFormat="MM/dd/yyyy" tooltip={recommendedIrqTargetCompletionDate ? DUE_DILIGENCE_FINAL_REVIEW_RECOMMENDED_IRQ_DUE_DATE : `Based on a ${riskRatingAsString(props.inherentRiskScore)} inherent risk rating, it is recommended that a follow-up due diligence workflow not be performed.`} />
                <Text noStyles color="darkGray">
                    Recommended: {recommendedIrqTargetCompletionDate ? moment(recommendedIrqTargetCompletionDate).format('MM/DD/YYYY') : 'no follow-up due diligence workflow'}
                </Text>
            </div>
            <div>
                <FormFieldDatePicker formFieldId="new-assessment-due-date" formFieldLabel="Due Diligence Workflow Due Date" selected={newAssessmentDueDate} handleChange={(value: Date | undefined) => setNewAssessmentDueDate(value)} dateFormat="MM/dd/yyyy" tooltip={recommendedAssessmentDueDate === null ? `Based on a ${riskRatingAsString(props.inherentRiskScore)} inherent risk rating, it is recommended that a follow-up due diligence workflow not be performed.` : `Based on a ${riskRatingAsString(props.inherentRiskScore)} inherent risk rating, the frequency for conducting a due diligence workflow for this ${assessmentSubjectTerm} is every ${getRecommendedReassessmentIntervalInMonths(props.inherentRiskScore, props.timeframes.service_reassessment_intervals)} months.`} />
                <Text noStyles color="darkGray">
                    Recommended: {recommendedAssessmentDueDate ? moment(recommendedAssessmentDueDate).format('MM/DD/YYYY') : 'no follow-up due diligence workflow'}
                </Text>
            </div>
        </ConfirmationModal>
    );
};

const getRecommendedAssessmentDueDate = (inherentRiskScore: RiskRating, assessmentIntervals: ReassessmentIntervals): Date | null => {
    const intervalMonths = getRecommendedReassessmentIntervalInMonths(inherentRiskScore, assessmentIntervals);

    if (!intervalMonths) {
        return null;
    }

    return moment().add(intervalMonths, 'months').toDate();
};

/**
 * Given the recommended due date for an assessment subject's due diligence workflow, and the expected times needed to complete assessments, works backwards to calculate the recommended inherent risk questionnaire due date.
 * It is assumed that assessments can be completed in parallel, so the length of time for assessments used in the calculation is whichever assessment is expected to take the longest for both respondents and internal users to complete.
 */
const getRecommendedIrqTargetDate = (recommendedAssessmentDate: Date | undefined, assessmentConfigurations: DueDiligenceAssessmentConfiguration[] | undefined, timeframes: DueDiligenceTimeframes): Date | undefined | null => {
    if (!assessmentConfigurations) {
        return undefined;
    } else if (!recommendedAssessmentDate) {
        return null;
    }

    const daysNeededToCompleteAllAssessmentsInParallel = max(assessmentConfigurations.map((assessment) => (assessment.expected_days_to_complete_questionnaire ?? 0) + (assessment.expected_days_to_complete_assessment ?? 0)));
    const daysNeededBeforeAssessmentSubmission = (timeframes.expected_days_to_complete_irq ?? 0) + (daysNeededToCompleteAllAssessmentsInParallel ?? 0) + (timeframes.expected_days_to_complete_final_review ?? 0);

    return moment(recommendedAssessmentDate).subtract(daysNeededBeforeAssessmentSubmission, 'days').toDate();
};
